<template>
  <main class="p-5 bg-echoes-background-black">
    <AdminHeader>
      <template #title>Eval Runs Queue Management</template>
      <template #description>Monitor and manage the eval runs processing queue</template>
      <template #actions>
        <div class="flex gap-2 flex-wrap">
          <button 
            @click="handleRefreshAll" 
            :disabled="loading"
            class="px-3 py-1.5 bg-echoes-primary hover:bg-echoes-primary/80 text-white rounded-md transition-colors duration-200 flex items-center gap-1.5 disabled:opacity-50 text-sm"
          >
            <i class="mdi mdi-refresh" :class="{ 'animate-spin': loading }"></i>
            Refresh
          </button>
          <button 
            @click="pauseQueue" 
            :disabled="loading || queueHealth?.isPaused"
            class="px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded-md transition-colors duration-200 flex items-center gap-1.5 disabled:opacity-50 text-sm"
          >
            <i class="mdi mdi-pause"></i>
            Pause
          </button>
          <button 
            @click="resumeQueue" 
            :disabled="loading || !queueHealth?.isPaused"
            class="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors duration-200 flex items-center gap-1.5 disabled:opacity-50 text-sm"
          >
            <i class="mdi mdi-play"></i>
            Resume
          </button>
          <button 
            @click="retryFailedJobs" 
            :disabled="loading"
            class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors duration-200 flex items-center gap-1.5 disabled:opacity-50 text-sm"
          >
            <i class="mdi mdi-refresh"></i>
            Retry Failed
          </button>
          <button 
            @click="clearAllJobs" 
            :disabled="loading"
            class="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors duration-200 flex items-center gap-1.5 disabled:opacity-50 text-sm"
          >
            <i class="mdi mdi-delete"></i>
            Clear All
          </button>
        </div>
      </template>
    </AdminHeader>

    <div class="pb-8">
      <!-- New Queue Management Sections -->
      <div class="mb-8 space-y-6">
        <div
          v-for="queueSection in queueSections"
          :key="queueSection.type"
          class="bg-echoes-background-gray rounded-lg p-6 border border-echoes-border-gray"
        >
          <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 class="text-lg font-semibold text-white flex items-center gap-2">
              <i class="mdi text-echoes-primary" :class="queueSection.icon"></i>
              {{ queueSection.label }} Queue Management
            </h3>
            <button
              @click="fetchQueueStats(queueSection.type)"
              :disabled="getQueueState(queueSection.type).loadingStats"
              class="px-3 py-1.5 bg-echoes-primary hover:bg-echoes-primary/80 text-white rounded-md transition-colors duration-200 flex items-center gap-1.5 disabled:opacity-50 text-sm"
            >
              <i class="mdi mdi-refresh" :class="{ 'animate-spin': getQueueState(queueSection.type).loadingStats }"></i>
              Refresh Stats
            </button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
              <p class="text-sm text-white/80">Queue Name</p>
              <p class="text-lg font-semibold text-white">
                {{ getQueueState(queueSection.type).stats?.queueName || '-' }}
              </p>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
              <p class="text-sm text-white/80">Waiting</p>
              <p class="text-2xl font-bold text-blue-500">
                {{ getQueueState(queueSection.type).stats?.queue?.waiting ?? 0 }}
              </p>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
              <p class="text-sm text-white/80">Active</p>
              <p class="text-2xl font-bold text-yellow-500">
                {{ getQueueState(queueSection.type).stats?.queue?.active ?? 0 }}
              </p>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
              <p class="text-sm text-white/80">Failed</p>
              <p class="text-2xl font-bold text-red-500">
                {{ getQueueState(queueSection.type).stats?.queue?.failed ?? 0 }}
              </p>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
              <p class="text-sm text-white/80">Delayed</p>
              <p class="text-2xl font-bold text-purple-500">
                {{ getQueueState(queueSection.type).stats?.queue?.delayed ?? 0 }}
              </p>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
              <p class="text-sm text-white/80">Completed</p>
              <p class="text-2xl font-bold text-green-500">
                {{ getQueueState(queueSection.type).stats?.queue?.completed ?? 0 }}
              </p>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
              <p class="text-sm text-white/80">Total</p>
              <p class="text-2xl font-bold text-white">
                {{ getQueueState(queueSection.type).stats?.queue?.total ?? 0 }}
              </p>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
              <p class="text-sm text-white/80">Semaphore</p>
              <p class="text-sm text-white">
                Slots: {{ getQueueState(queueSection.type).stats?.semaphore?.availableSlots ?? 0 }} /
                {{ getQueueState(queueSection.type).stats?.semaphore?.maxConcurrent ?? 0 }}
              </p>
              <p class="text-sm text-white/80">
                Active: {{ getQueueState(queueSection.type).stats?.semaphore?.activeJobs ?? 0 }}
              </p>
              <p class="text-xs text-white/70 mt-1 break-all">
                Active IDs:
                {{ formatActiveJobsList(getQueueState(queueSection.type).stats?.semaphore?.activeJobsList) }}
              </p>
            </div>
          </div>

          <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray mb-4">
            <p class="text-sm text-white/80 mb-3">Queue Actions</p>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div class="flex gap-2">
                <input
                  v-model="queueManagementState[queueSection.type].releaseId"
                  type="text"
                  placeholder="ID to release semaphore slot"
                  class="w-full px-3 py-2 bg-echoes-background-gray border border-echoes-border-gray rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-echoes-primary"
                />
                <button
                  @click="handleReleaseSlot(queueSection.type)"
                  :disabled="getQueueState(queueSection.type).loadingRelease"
                  class="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors duration-200 flex items-center gap-1.5 disabled:opacity-50 text-sm whitespace-nowrap"
                >
                  <i class="mdi mdi-key-plus"></i>
                  Release
                </button>
              </div>

              <div class="flex flex-wrap gap-2">
                <button
                  @click="handleResetSemaphore(queueSection.type)"
                  :disabled="getQueueState(queueSection.type).loadingReset"
                  class="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md transition-colors duration-200 flex items-center gap-1.5 disabled:opacity-50 text-sm"
                >
                  <i class="mdi mdi-lock-reset"></i>
                  Reset Semaphore
                </button>
                <button
                  @click="handleCleanQueue(queueSection.type)"
                  :disabled="getQueueState(queueSection.type).loadingClean"
                  class="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors duration-200 flex items-center gap-1.5 disabled:opacity-50 text-sm"
                >
                  <i class="mdi mdi-broom"></i>
                  Clean Queue
                </button>
              </div>
            </div>
          </div>

          <div v-if="getQueueState(queueSection.type).errorMessage" class="mb-3 text-sm text-red-400">
            {{ getQueueState(queueSection.type).errorMessage }}
          </div>
          <div v-if="getQueueState(queueSection.type).successMessage" class="mb-3 text-sm text-green-400">
            {{ getQueueState(queueSection.type).successMessage }}
          </div>
          <div
            v-if="getQueueState(queueSection.type).lastActionPayload"
            class="bg-echoes-background-black rounded-lg border border-echoes-border-gray p-4"
          >
            <p class="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3 flex items-center gap-2">
              <i class="mdi mdi-check-circle-outline text-green-400"></i>
              Last Action Result
            </p>

            <!-- Identity row -->
            <div class="flex flex-wrap gap-2 mb-4">
              <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-echoes-background-gray border border-echoes-border-gray text-xs text-white/80">
                <i class="mdi mdi-identifier text-white/40"></i>
                Queue: <span class="text-white font-medium">{{ getQueueState(queueSection.type).lastActionPayload.queueType ?? '—' }}</span>
              </span>
              <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-echoes-background-gray border border-echoes-border-gray text-xs text-white/80">
                <i class="mdi mdi-tag-outline text-white/40"></i>
                Name: <span class="text-white font-medium">{{ getQueueState(queueSection.type).lastActionPayload.queueName ?? '—' }}</span>
              </span>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <!-- Semaphore card -->
              <div
                v-if="getQueueState(queueSection.type).lastActionPayload.semaphore"
                class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray p-3"
              >
                <p class="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <i class="mdi mdi-lock-outline text-amber-400"></i>
                  Semaphore
                </p>
                <div class="space-y-2">
                  <!-- Available / Max slots bar -->
                  <div>
                    <div class="flex justify-between text-xs text-white/60 mb-1">
                      <span>Available Slots</span>
                      <span class="text-white font-medium">
                        {{ getQueueState(queueSection.type).lastActionPayload.semaphore.availableSlots }}
                        / {{ getQueueState(queueSection.type).lastActionPayload.semaphore.maxConcurrent }}
                      </span>
                    </div>
                    <div class="h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div
                        class="h-full rounded-full transition-all duration-500"
                        :class="getQueueState(queueSection.type).lastActionPayload.semaphore.availableSlots > 0 ? 'bg-green-500' : 'bg-red-500'"
                        :style="{
                          width: getQueueState(queueSection.type).lastActionPayload.semaphore.maxConcurrent > 0
                            ? (getQueueState(queueSection.type).lastActionPayload.semaphore.availableSlots / getQueueState(queueSection.type).lastActionPayload.semaphore.maxConcurrent * 100) + '%'
                            : '0%'
                        }"
                      />
                    </div>
                  </div>
                  <div class="flex items-center justify-between text-xs">
                    <span class="text-white/60">Active Jobs</span>
                    <span
                      class="font-semibold"
                      :class="getQueueState(queueSection.type).lastActionPayload.semaphore.activeJobs > 0 ? 'text-yellow-400' : 'text-green-400'"
                    >
                      {{ getQueueState(queueSection.type).lastActionPayload.semaphore.activeJobs }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Queue counts card -->
              <div
                v-if="getQueueState(queueSection.type).lastActionPayload.queue"
                class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray p-3"
              >
                <p class="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <i class="mdi mdi-tray-full text-blue-400"></i>
                  Queue Counts
                </p>
                <div class="grid grid-cols-3 gap-x-3 gap-y-2">
                  <div class="text-center">
                    <p class="text-lg font-bold text-blue-400">{{ getQueueState(queueSection.type).lastActionPayload.queue.waiting }}</p>
                    <p class="text-xs text-white/50">Waiting</p>
                  </div>
                  <div class="text-center">
                    <p class="text-lg font-bold text-yellow-400">{{ getQueueState(queueSection.type).lastActionPayload.queue.active }}</p>
                    <p class="text-xs text-white/50">Active</p>
                  </div>
                  <div class="text-center">
                    <p class="text-lg font-bold text-purple-400">{{ getQueueState(queueSection.type).lastActionPayload.queue.delayed }}</p>
                    <p class="text-xs text-white/50">Delayed</p>
                  </div>
                  <div class="text-center">
                    <p class="text-lg font-bold text-red-400">{{ getQueueState(queueSection.type).lastActionPayload.queue.failed }}</p>
                    <p class="text-xs text-white/50">Failed</p>
                  </div>
                  <div class="text-center">
                    <p class="text-lg font-bold text-green-400">{{ getQueueState(queueSection.type).lastActionPayload.queue.completed }}</p>
                    <p class="text-xs text-white/50">Done</p>
                  </div>
                  <div class="text-center">
                    <p class="text-lg font-bold text-white">{{ getQueueState(queueSection.type).lastActionPayload.queue.total }}</p>
                    <p class="text-xs text-white/50">Total</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Queue Health Status -->
      <div class="mb-8">
        <div class="bg-echoes-background-gray rounded-lg p-6 border border-echoes-border-gray">
          <h3 class="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <i class="mdi mdi-heart-pulse text-echoes-primary"></i>
            Queue Health Status
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-white/80">Status</p>
                  <p class="text-lg font-semibold" :class="getStatusColor(queueHealth?.status || 'unknown')">
                    {{ (queueHealth?.status || 'unknown').toUpperCase() }}
                  </p>
                </div>
                <i class="mdi mdi-circle text-2xl" :class="getStatusColor(queueHealth?.status || 'unknown')"></i>
              </div>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-white/80">Is Paused</p>
                  <p class="text-lg font-semibold" :class="queueHealth?.isPaused ? 'text-yellow-500' : 'text-green-500'">
                    {{ queueHealth?.isPaused ? 'YES' : 'NO' }}
                  </p>
                </div>
                <i class="mdi" :class="queueHealth?.isPaused ? 'mdi-pause text-yellow-500' : 'mdi-play text-green-500'"></i>
              </div>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-white/80">Failure Rate</p>
                  <p class="text-lg font-semibold" :class="getFailureRateColor(queueHealth?.failureRate || 0)">
                    {{ queueHealth?.failureRate || 0 }}%
                  </p>
                </div>
                <i class="mdi mdi-chart-line text-2xl" :class="getFailureRateColor(queueHealth?.failureRate || 0)"></i>
              </div>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-white/80">Total Jobs</p>
                  <p class="text-lg font-semibold text-white">{{ queueHealth?.totalJobs || 0 }}</p>
                </div>
                <i class="mdi mdi-counter text-2xl text-white"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Job Counts -->
      <div class="mb-8">
        <div class="bg-echoes-background-gray rounded-lg p-6 border border-echoes-border-gray">
          <h3 class="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <i class="mdi mdi-chart-bar text-echoes-primary"></i>
            Job Counts
          </h3>
          <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray text-center">
              <p class="text-sm text-white/80">Waiting</p>
              <p class="text-2xl font-bold text-blue-500">{{ jobCounts?.waiting || 0 }}</p>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray text-center">
              <p class="text-sm text-white/80">Active</p>
              <p class="text-2xl font-bold text-yellow-500">{{ jobCounts?.active || 0 }}</p>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray text-center">
              <p class="text-sm text-white/80">Completed</p>
              <p class="text-2xl font-bold text-green-500">{{ jobCounts?.completed || 0 }}</p>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray text-center">
              <p class="text-sm text-white/80">Failed</p>
              <p class="text-2xl font-bold text-red-500">{{ jobCounts?.failed || 0 }}</p>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray text-center">
              <p class="text-sm text-white/80">Delayed</p>
              <p class="text-2xl font-bold text-purple-500">{{ jobCounts?.delayed || 0 }}</p>
            </div>
          </div>
        </div>
      </div>


      <!-- Recent Activity -->
      <div class="mb-8">
        <div class="bg-echoes-background-gray rounded-lg p-6 border border-echoes-border-gray">
          <h3 class="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <i class="mdi mdi-history text-echoes-primary"></i>
            Recent Activity (Last 24 Hours)
          </h3>
          <div class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray text-center">
                <p class="text-sm text-white/80">Total Jobs</p>
                <p class="text-2xl font-bold text-white">{{ recentActivity?.totalJobs || 0 }}</p>
              </div>
              <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray text-center">
                <p class="text-sm text-white/80">Completed</p>
                <p class="text-2xl font-bold text-green-500">{{ recentActivity?.completed || 0 }}</p>
              </div>
              <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray text-center">
                <p class="text-sm text-white/80">Failed</p>
                <p class="text-2xl font-bold text-red-500">{{ recentActivity?.failed || 0 }}</p>
              </div>
            </div>
            
            <div v-if="recentActivity?.jobs && recentActivity.jobs.length > 0" class="space-y-2">
              <h4 class="text-md font-medium text-white">Recent Jobs</h4>
              <div class="bg-echoes-background-black rounded-lg border border-echoes-border-gray overflow-hidden">
                <div class="overflow-x-auto">
                  <table class="w-full">
                    <thead class="bg-echoes-background-gray">
                      <tr>
                        <th class="px-4 py-3 text-left text-sm font-medium text-white">Job ID</th>
                        <th class="px-4 py-3 text-left text-sm font-medium text-white">Name</th>
                        <th class="px-4 py-3 text-left text-sm font-medium text-white">Status</th>
                        <th class="px-4 py-3 text-left text-sm font-medium text-white">Timestamp</th>
                        <th class="px-4 py-3 text-left text-sm font-medium text-white">Failed Reason</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-echoes-border-gray">
                      <tr v-for="job in recentActivity.jobs.slice(0, 10)" :key="job.id" class="hover:bg-echoes-background-gray/50">
                        <td class="px-4 py-3 text-sm text-white/80">{{ job.id }}</td>
                        <td class="px-4 py-3 text-sm text-white">{{ job.name }}</td>
                        <td class="px-4 py-3 text-sm">
                          <span class="px-2 py-1 rounded-full text-xs font-medium" 
                                :class="job.status === 'completed' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'">
                            {{ job.status }}
                          </span>
                        </td>
                        <td class="px-4 py-3 text-sm text-white/80">{{ formatTimestamp(job.timestamp) }}</td>
                        <td class="px-4 py-3 text-sm text-red-500">{{ job.failedReason || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Jobs Management -->
      <div class="mb-8">
        <div class="bg-echoes-background-gray rounded-lg p-6 border border-echoes-border-gray">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-white flex items-center gap-2">
              <i class="mdi mdi-format-list-bulleted text-echoes-primary"></i>
              Jobs Management
            </h3>
            <div class="flex gap-2">
              <select 
                v-model="selectedJobType" 
                @change="filterJobs"
                class="px-3 py-2 bg-echoes-background-black border border-echoes-border-gray rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-echoes-primary"
              >
                <option value="">All Job Types</option>
                <option value="PROCESS_EVAL_RUN">Process Eval Run</option>
                <option value="CLEANUP_TASK_BUNDLES">Cleanup Task Bundles</option>
                <option value="CLEANUP_MODEL_RESPONSE">Cleanup Model Response</option>
                <option value="SEND_TO_ASYMM_EVAL">Send to Asymm Eval</option>
              </select>
              <select 
                v-model="selectedJobStatus" 
                @change="filterJobs"
                class="px-3 py-2 bg-echoes-background-black border border-echoes-border-gray rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-echoes-primary"
              >
                <option value="">All Status</option>
                <option value="waiting">Waiting</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="delayed">Delayed</option>
              </select>
              <button 
                @click="loadJobs"
                :disabled="loadingJobs"
                class="px-4 py-2 bg-echoes-primary hover:bg-echoes-primary/80 text-white rounded-md transition-colors duration-200 flex items-center gap-2 disabled:opacity-50"
              >
                <i class="mdi mdi-refresh" :class="{ 'animate-spin': loadingJobs }"></i>
                Refresh Jobs
              </button>
            </div>
          </div>

          <!-- Jobs Table -->
          <div v-if="filteredJobs.length > 0" class="bg-echoes-background-black rounded-lg border border-echoes-border-gray overflow-hidden">
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-echoes-background-gray">
                  <tr>
                    <th class="px-4 py-3 text-left text-sm font-medium text-white">Job ID</th>
                    <th class="px-4 py-3 text-left text-sm font-medium text-white">Type</th>
                    <th class="px-4 py-3 text-left text-sm font-medium text-white">Status</th>
                    <th class="px-4 py-3 text-left text-sm font-medium text-white">Created</th>
                    <th class="px-4 py-3 text-left text-sm font-medium text-white">Progress</th>
                    <th class="px-4 py-3 text-left text-sm font-medium text-white">Actions</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-echoes-border-gray">
                  <tr v-for="job in paginatedJobs" :key="job.id" class="hover:bg-echoes-background-gray/50">
                    <td class="px-4 py-3 text-sm text-white/80 font-mono">{{ job.id }}</td>
                    <td class="px-4 py-3 text-sm text-white">{{ job.name }}</td>
                    <td class="px-4 py-3 text-sm">
                      <span class="px-2 py-1 rounded-full text-xs font-medium" 
                            :class="getStatusBadgeClass(job.state)">
                        {{ job.state }}
                      </span>
                    </td>
                    <td class="px-4 py-3 text-sm text-white/80">{{ formatTimestamp(job.timestamp) }}</td>
                    <td class="px-4 py-3 text-sm">
                      <div class="flex items-center gap-2">
                        <div class="w-16 bg-echoes-background-gray rounded-full h-2">
                          <div 
                            class="bg-echoes-primary h-2 rounded-full transition-all duration-300" 
                            :style="{ width: `${job.progress || 0}%` }"
                          ></div>
                        </div>
                        <span class="text-white/80 text-xs">{{ job.progress || 0 }}%</span>
                      </div>
                    </td>
                    <td class="px-4 py-3 text-sm">
                      <div class="flex gap-2">
                        <button 
                          @click="viewJobDetails(job.id)"
                          class="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs transition-colors duration-200"
                        >
                          <i class="mdi mdi-eye"></i>
                        </button>
                        <button 
                          v-if="job.state === 'failed'"
                          @click="retryJob(job.id)"
                          class="px-2 py-1 bg-orange-600 hover:bg-orange-700 text-white rounded text-xs transition-colors duration-200"
                        >
                          <i class="mdi mdi-refresh"></i>
                        </button>
                        <button 
                          @click="removeJob(job.id)"
                          class="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs transition-colors duration-200"
                        >
                          <i class="mdi mdi-delete"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- Pagination -->
            <div v-if="totalPages > 1" class="bg-echoes-background-gray border-t border-echoes-border-gray px-4 py-3">
              <div class="flex items-center justify-between">
                <div class="text-sm text-white/80">
                  Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to {{ Math.min(currentPage * itemsPerPage, filteredJobs.length) }} of {{ filteredJobs.length }} jobs
                </div>
                <div class="flex gap-2">
                  <button 
                    @click="currentPage = Math.max(1, currentPage - 1)"
                    :disabled="currentPage === 1"
                    class="px-3 py-1 rounded text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span class="px-3 py-1 text-sm text-white">
                    Page {{ currentPage }} of {{ totalPages }}
                  </span>
                  <button 
                    @click="currentPage = Math.min(totalPages, currentPage + 1)"
                    :disabled="currentPage === totalPages"
                    class="px-3 py-1 rounded text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div v-else class="text-center py-8 text-white/60">
            <i class="mdi mdi-inbox text-4xl mb-2"></i>
            <p>No jobs found matching the current filters</p>
          </div>
        </div>
      </div>

      <!-- Job Details Modal -->
      <div v-if="selectedJob" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex items-center justify-center min-h-screen px-4">
          <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="selectedJob = null"></div>
          <div class="relative bg-echoes-background-gray border border-echoes-border-gray rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <!-- Modal Header -->
            <div class="flex justify-between items-center px-6 py-4 border-b border-echoes-border-gray">
              <div>
                <h2 class="text-xl font-semibold text-white">Job Details</h2>
                <p class="text-sm text-white/80">Job ID: {{ selectedJob.id }}</p>
              </div>
              <button 
                @click="selectedJob = null"
                class="text-white/60 hover:text-white transition-colors duration-200"
              >
                <i class="mdi mdi-close text-xl"></i>
              </button>
            </div>
            
            <!-- Modal Content -->
            <div class="px-6 py-4 space-y-6">
              <!-- Basic Info -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
                  <h4 class="text-sm font-medium text-white/80 mb-2">Job Type</h4>
                  <p class="text-white">{{ selectedJob.name }}</p>
                </div>
                <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
                  <h4 class="text-sm font-medium text-white/80 mb-2">Status</h4>
                  <span class="px-2 py-1 rounded-full text-xs font-medium" 
                        :class="getStatusBadgeClass(selectedJob.state)">
                    {{ selectedJob.state }}
                  </span>
                </div>
                <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
                  <h4 class="text-sm font-medium text-white/80 mb-2">Created</h4>
                  <p class="text-white">{{ formatTimestamp(selectedJob.timestamp) }}</p>
                </div>
                <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
                  <h4 class="text-sm font-medium text-white/80 mb-2">Progress</h4>
                  <div class="flex items-center gap-2">
                    <div class="w-full bg-echoes-background-gray rounded-full h-2">
                      <div 
                        class="bg-echoes-primary h-2 rounded-full transition-all duration-300" 
                        :style="{ width: `${selectedJob.progress || 0}%` }"
                      ></div>
                    </div>
                    <span class="text-white text-sm">{{ selectedJob.progress || 0 }}%</span>
                  </div>
                </div>
              </div>

              <!-- Job Data -->
              <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
                <h4 class="text-sm font-medium text-white/80 mb-2">Job Data</h4>
                <pre class="text-xs text-white/80 bg-echoes-background-gray p-3 rounded overflow-x-auto">{{ JSON.stringify(selectedJob.data, null, 2) }}</pre>
              </div>

              <!-- Error Info (if failed) -->
              <div v-if="selectedJob.failedReason" class="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <h4 class="text-sm font-medium text-red-400 mb-2">Error Details</h4>
                <p class="text-red-300 text-sm">{{ selectedJob.failedReason }}</p>
              </div>

              <!-- Return Value (if completed) -->
              <div v-if="selectedJob.returnvalue" class="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                <h4 class="text-sm font-medium text-green-400 mb-2">Return Value</h4>
                <pre class="text-xs text-green-300 bg-green-500/10 p-3 rounded overflow-x-auto">{{ JSON.stringify(selectedJob.returnvalue, null, 2) }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Asymm Configuration Test -->
      <div class="mb-8">
        <div class="bg-echoes-background-gray rounded-lg p-6 border border-echoes-border-gray">
          <h3 class="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <i class="mdi mdi-test-tube text-echoes-primary"></i>
            Asymm Configuration
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-white/80">URL Configured</p>
                  <p class="text-lg font-semibold" :class="asymmConfig?.hasUrl ? 'text-green-500' : 'text-red-500'">
                    {{ asymmConfig?.hasUrl ? 'YES' : 'NO' }}
                  </p>
                </div>
                <i class="mdi mdi-web text-2xl" :class="asymmConfig?.hasUrl ? 'text-green-500' : 'text-red-500'"></i>
              </div>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-white/80">Key Configured</p>
                  <p class="text-lg font-semibold" :class="asymmConfig?.hasKey ? 'text-green-500' : 'text-red-500'">
                    {{ asymmConfig?.hasKey ? 'YES' : 'NO' }}
                  </p>
                </div>
                <i class="mdi mdi-key text-2xl" :class="asymmConfig?.hasKey ? 'text-green-500' : 'text-red-500'"></i>
              </div>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-white/80">Environment</p>
                  <p class="text-lg font-semibold text-white">{{ asymmConfig?.appEnv || 'unknown' }}</p>
                </div>
                <i class="mdi mdi-server text-2xl text-white"></i>
              </div>
            </div>
            <div class="bg-echoes-background-black rounded-lg p-4 border border-echoes-border-gray">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-white/80">Will Use Mock</p>
                  <p class="text-lg font-semibold" :class="asymmConfig?.willUseMock ? 'text-yellow-500' : 'text-blue-500'">
                    {{ asymmConfig?.willUseMock ? 'YES' : 'NO' }}
                  </p>
                </div>
                <i class="mdi mdi-test-tube text-2xl" :class="asymmConfig?.willUseMock ? 'text-yellow-500' : 'text-blue-500'"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import AdminHeader from '@/pages/admin/AdminHeader.vue';
import { useEvalRunsQueue } from '@/composables/use-eval-runs-queue';
import {
  getStats,
  cleanQueue,
  resetSemaphore,
  releaseSlot,
} from '@/pages/admin/actions/QueueManagementService';

const { 
  loading,
  queueHealth,
  jobCounts,
  recentActivity,
  asymmConfig,
  refreshData,
  pauseQueue,
  resumeQueue,
  retryFailedJobs,
  clearAllJobs,
  getJobsByStatus,
  getJobDetails
} = useEvalRunsQueue();

// Jobs management state
const allJobs = ref([]);
const filteredJobs = ref([]);
const selectedJob = ref(null);
const loadingJobs = ref(false);
const selectedJobType = ref('');
const selectedJobStatus = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10);

const queueSections = [
  { type: 'eval-runs', label: 'Eval Runs', icon: 'mdi-cog-transfer' },
  { type: 'leaderboard', label: 'Leaderboard', icon: 'mdi-trophy-outline' },
];

const createQueueState = () => ({
  stats: null,
  releaseId: '',
  loadingStats: false,
  loadingClean: false,
  loadingReset: false,
  loadingRelease: false,
  errorMessage: '',
  successMessage: '',
  lastActionPayload: null,
});

const queueManagementState = ref({
  'eval-runs': createQueueState(),
  leaderboard: createQueueState(),
});

// Computed properties
const paginatedJobs = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredJobs.value.slice(start, end);
});

const totalPages = computed(() => {
  return Math.ceil(filteredJobs.value.length / itemsPerPage.value);
});

// Methods
const getStatusColor = (status) => {
  switch (status) {
    case 'healthy': return 'text-green-500';
    case 'warning': return 'text-yellow-500';
    case 'critical': return 'text-red-500';
    default: return 'text-white';
  }
};

const getFailureRateColor = (rate) => {
  if (rate > 50) return 'text-red-500';
  if (rate > 20) return 'text-yellow-500';
  return 'text-green-500';
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'completed': return 'bg-green-500/20 text-green-500';
    case 'failed': return 'bg-red-500/20 text-red-500';
    case 'active': return 'bg-yellow-500/20 text-yellow-500';
    case 'waiting': return 'bg-blue-500/20 text-blue-500';
    case 'delayed': return 'bg-purple-500/20 text-purple-500';
    default: return 'bg-gray-500/20 text-gray-500';
  }
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '-';
  return new Date(timestamp).toLocaleString();
};

const formatActiveJobsList = (activeJobsList) => {
  if (!activeJobsList || typeof activeJobsList !== 'object') {
    return '{}';
  }

  const activeIds = Object.keys(activeJobsList);
  if (!activeIds.length) {
    return '{}';
  }

  return '{' + activeIds.map((id) => '"' + id + '"').join(', ') + '}';
};

const getQueueState = (queueType) => queueManagementState.value[queueType];

const getErrorMessage = (error, fallbackMessage) => {
  return error?.response?.data?.message || error?.message || fallbackMessage;
};

const clearQueueFeedback = (queueType) => {
  const state = getQueueState(queueType);
  state.errorMessage = '';
  state.successMessage = '';
  state.lastActionPayload = null;
};

const fetchQueueStats = async (queueType) => {
  const state = getQueueState(queueType);
  state.loadingStats = true;
  clearQueueFeedback(queueType);

  try {
    const response = await getStats(queueType);
    if (response) {
      state.stats = response;
      state.successMessage = 'Queue stats refreshed successfully';
      state.lastActionPayload = response;
    } else {
      state.errorMessage = 'Failed to fetch queue stats';
    }
  } catch (error) {
    state.errorMessage = getErrorMessage(error, 'Failed to fetch queue stats');
  } finally {
    state.loadingStats = false;
  }
};

const withQueueAction = async (queueType, loadingKey, successFallbackMessage, actionFn) => {
  const state = getQueueState(queueType);
  state[loadingKey] = true;
  clearQueueFeedback(queueType);

  try {
    const response = await actionFn();
    if (response) {
      state.successMessage = response.message || successFallbackMessage;
      state.lastActionPayload = response;
      await fetchQueueStats(queueType);
    } else {
      state.errorMessage = 'Request failed';
    }
  } catch (error) {
    state.errorMessage = getErrorMessage(error, 'Request failed');
  } finally {
    state[loadingKey] = false;
  }
};

const handleCleanQueue = async (queueType) => {
  await withQueueAction(
    queueType,
    'loadingClean',
    'Queue cleaned successfully',
    () => cleanQueue(queueType),
  );
};

const handleResetSemaphore = async (queueType) => {
  await withQueueAction(
    queueType,
    'loadingReset',
    'Semaphore reset successfully',
    () => resetSemaphore(queueType),
  );
};

const handleReleaseSlot = async (queueType) => {
  const state = getQueueState(queueType);
  if (!state.releaseId?.toString().trim()) {
    state.errorMessage = 'Please provide an ID to release semaphore slot';
    state.successMessage = '';
    state.lastActionPayload = null;
    return;
  }

  await withQueueAction(
    queueType,
    'loadingRelease',
    'Semaphore slot released successfully',
    () => releaseSlot(queueType, state.releaseId),
  );
};

const refreshAllQueueStats = async () => {
  await Promise.all(queueSections.map((section) => fetchQueueStats(section.type)));
};

const handleRefreshAll = async () => {
  await Promise.all([refreshData(), refreshAllQueueStats()]);
};

const loadJobs = async () => {
  loadingJobs.value = true;
  try {
    const statuses = ['waiting', 'active', 'completed', 'failed', 'delayed'];
    const allJobsData = [];
    
    for (const status of statuses) {
      try {
        const jobs = await getJobsByStatus(status, 50);
        if (jobs.jobs) {
          allJobsData.push(...jobs.jobs);
        }
      } catch (error) {
        console.error(`Error loading ${status} jobs:`, error);
      }
    }
    
    allJobs.value = allJobsData;
    filterJobs();
  } catch (error) {
    console.error('Error loading jobs:', error);
  } finally {
    loadingJobs.value = false;
  }
};

const filterJobs = () => {
  let filtered = [...allJobs.value];
  
  if (selectedJobType.value) {
    filtered = filtered.filter(job => job.name === selectedJobType.value);
  }
  
  if (selectedJobStatus.value) {
    filtered = filtered.filter(job => job.state === selectedJobStatus.value);
  }
  
  // Sort by timestamp (newest first)
  filtered.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  
  filteredJobs.value = filtered;
  currentPage.value = 1; // Reset to first page when filtering
};

const viewJobDetails = async (jobId) => {
  try {
    const jobDetails = await getJobDetails(jobId);
    selectedJob.value = jobDetails;
  } catch (error) {
    console.error('Error loading job details:', error);
  }
};

const retryJob = async (jobId) => {
  if (!confirm('Are you sure you want to retry this job?')) {
    return;
  }
  
  try {
    // This would need to be implemented in the API
    console.log('Retrying job:', jobId);
    await loadJobs(); // Refresh the jobs list
  } catch (error) {
    console.error('Error retrying job:', error);
  }
};

const removeJob = async (jobId) => {
  if (!confirm('Are you sure you want to remove this job? This action cannot be undone.')) {
    return;
  }
  
  try {
    // This would need to be implemented in the API
    console.log('Removing job:', jobId);
    await loadJobs(); // Refresh the jobs list
  } catch (error) {
    console.error('Error removing job:', error);
  }
};

onMounted(() => {
  handleRefreshAll();
  loadJobs();
});
</script>
